import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Shared parallax engine for all hero blocks.
 *
 * @param {object}    config
 * @param {string}    config.selector            CSS selector for the hero root element.
 * @param {Array}    [config.extraMouseTargets]  Mouse-parallax targets appended after the base four
 *                                               layer targets. Each: { selector, xMult, yMult, lerp, live? }
 * @param {Array}    [config.scrollTargets]      Elements to scroll-parallax. Each: { selector, yPercent }
 * @param {Function} [config.onEntranceReady]    Called with (hero, tl) after base layer entrance is
 *                                               queued. Hook in graphic entrances and tl.call() entries here.
 */
export function initHeroParallax({
    selector,
    extraMouseTargets = [],
    scrollTargets     = [],
    onEntranceReady   = null,
}) {
    const hero = document.querySelector(selector);
    if (!hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // ── Layer queries ──────────────────────────────────────────────────────
    const baseLayer     = hero.querySelector('[data-parallax-layer="1"]');
    const overlayLayers = [2, 3, 4]
        .map(n => hero.querySelector(`[data-parallax-layer="${n}"]`))
        .filter(Boolean);

    // Pre-scale 1.08× — bleed covers the MAX_X:12 / MAX_Y:8 mouse-shift budget.
    if (overlayLayers.length) {
        gsap.set(overlayLayers, { scale: 1.08, transformOrigin: 'center center' });
    }

    // ── Scroll trigger defaults ────────────────────────────────────────────
    const triggerDefaults = {
        trigger: hero,
        start:   'top top',
        end:     'bottom top',
        scrub:   true,
    };

    // Declared early so the tl.onComplete closure can reference it by the time
    // it fires (well after mouseTargets is assigned below).
    let mouseTargets = [];

    // ── Entrance timeline ──────────────────────────────────────────────────
    const tl = gsap.timeline({
        onComplete() {
            mouseTargets.forEach(t => { if (!t.live) { t.curX = 0; t.curY = 0; } });
        },
    });

    // Layers 2–4: drift up from 10px, hold pre-scale.
    overlayLayers.forEach((layer, i) => {
        tl.fromTo(
            layer,
            { y: 10 },
            { y: 0, scale: 1.08, duration: 0.9, ease: 'power2.out' },
            i === 0 ? '0' : '>-0.7',
        );
    });

    // Hero-specific entrance hooks (graphics, tl.call() entries, etc.)
    onEntranceReady?.(hero, tl);

    // ── Mouse parallax targets ─────────────────────────────────────────────
    mouseTargets = [
        { el: baseLayer,        xMult: 0,    yMult: 0,    lerp: 0.10  },
        { el: overlayLayers[0], xMult: 0.4,  yMult: 0.3,  lerp: 0.07  },
        { el: overlayLayers[1], xMult: 0.7,  yMult: 0.5,  lerp: 0.055 },
        { el: overlayLayers[2], xMult: 1.0,  yMult: 0.7,  lerp: 0.04  },
        ...extraMouseTargets.map(t => ({ ...t, el: hero.querySelector(t.selector) })),
    ].filter(t => t.el);

    mouseTargets.forEach(t => { t.curX = 0; t.curY = 0; });

    const MAX_X = 12;
    const MAX_Y = 8;

    let targetNormX = 0;
    let targetNormY = 0;

    // Live targets move immediately; all others gate until entrance completes.
    gsap.ticker.add(() => {
        mouseTargets.forEach(t => {
            const destX = targetNormX * MAX_X * t.xMult;
            const destY = targetNormY * MAX_Y * t.yMult;
            t.curX += (destX - t.curX) * t.lerp;
            t.curY += (destY - t.curY) * t.lerp;
            if (t.live || !tl.isActive()) {
                gsap.set(t.el, { x: t.curX, y: t.curY });
            }
        });
    });

    const heroLayers = hero.querySelector('.hero-layers');

    heroLayers?.addEventListener('mousemove', e => {
        const rect  = heroLayers.getBoundingClientRect();
        targetNormX = ((e.clientX - rect.left) / rect.width  - 0.5) * 2;
        targetNormY = ((e.clientY - rect.top)  / rect.height - 0.5) * 2;
    });

    heroLayers?.addEventListener('mouseleave', () => {
        targetNormX = 0;
        targetNormY = 0;
    });

    // ── Scroll parallax ────────────────────────────────────────────────────
    scrollTargets.forEach(({ selector: sel, yPercent }) => {
        const el = hero.querySelector(sel);
        if (el) gsap.to(el, { yPercent, ease: 'none', scrollTrigger: { ...triggerDefaults } });
    });
}
